import React from 'react';
import { GameSession, Player } from '../types/game';

interface ScoreBoardProps {
  session: GameSession;
  playerSymbol: Player;
  playerName?: string;
  opponentName?: string;
}

export const ScoreBoard: React.FC<ScoreBoardProps> = ({
  session,
  playerSymbol,
  playerName,
  opponentName
}) => {
  const opponentSymbol = playerSymbol === 'X' ? 'O' : 'X';

  const shortName = (name?: string, fallback?: string) => {
    const n = name || fallback || '';
    return n.length > 8 ? `${n.slice(0, 7)}…` : n;
  };

  return (
    <div className="w-full max-w-md mx-auto mb-4 sm:mb-6">
      {/* Compact mobile bar */}
      <div className="sm:hidden bg-retro-dark/50 border-2 border-retro-purple rounded-lg px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="font-retro text-[11px] text-neon-cyan">R{session.currentRound}/3</div>
          <div className="flex items-center gap-2">
            <span className={`font-retro text-xs ${
              playerSymbol === 'X' ? 'text-neon-pink' : 'text-neon-cyan'
            }`}>
              {shortName(playerName, playerSymbol)}<span className="opacity-60"> ({playerSymbol})</span>
            </span>
            <span className="font-retro text-base font-bold text-neon-yellow">
              {session.scores[playerSymbol]}
            </span>
            <span className="font-retro text-retro-orange text-xs">VS</span>
            <span className={`font-retro text-base font-bold text-neon-yellow`}>
              {session.scores[opponentSymbol]}
            </span>
            <span className={`font-retro text-xs ${
              opponentSymbol === 'X' ? 'text-neon-pink' : 'text-neon-cyan'
            }`}>
              {shortName(opponentName, opponentSymbol)}<span className="opacity-60"> ({opponentSymbol})</span>
            </span>
          </div>
        </div>
      </div>

      {/* Desktop / tablet layout */}
      <div className="hidden sm:block">
        {/* Round indicator */}
        <div className="text-center mb-3 sm:mb-4">
          <div className="inline-block bg-retro-dark border-2 border-neon-cyan rounded-lg px-3 py-2 sm:px-4">
            <p className="font-retro text-neon-cyan text-xs sm:text-sm">ROUND</p>
            <p className="font-retro text-neon-yellow text-xl sm:text-2xl font-bold animate-pulse">
              {session.currentRound}/3
            </p>
          </div>
        </div>

        {/* Score display */}
        <div className="flex justify-between items-center bg-retro-dark/50 border-2 border-retro-purple rounded-lg p-3 sm:p-4">
          {/* Player score */}
          <div className="text-center flex-1">
            <div className={`font-retro text-xl sm:text-2xl mb-1 sm:mb-2 ${playerSymbol === 'X' ? 'text-neon-pink' : 'text-neon-cyan'}`}>
              {playerSymbol}
            </div>
            <div className="text-2xl sm:text-3xl font-retro font-bold text-neon-yellow">
              {session.scores[playerSymbol]}
            </div>
            <div className="text-[10px] sm:text-xs font-retro text-gray-400 mt-1">TU</div>
          </div>

          {/* VS separator */}
          <div className="px-3 sm:px-4">
            <div className="font-retro text-retro-orange text-lg sm:text-xl animate-pulse">VS</div>
          </div>

          {/* Opponent score */}
          <div className="text-center flex-1">
            <div className={`font-retro text-xl sm:text-2xl mb-1 sm:mb-2 ${opponentSymbol === 'X' ? 'text-neon-pink' : 'text-neon-cyan'}`}>
              {opponentSymbol}
            </div>
            <div className="text-2xl sm:text-3xl font-retro font-bold text-neon-yellow">
              {session.scores[opponentSymbol]}
            </div>
            <div className="text-[10px] sm:text-xs font-retro text-gray-400 mt-1">OPONENTE</div>
          </div>
        </div>
      </div>
    </div>
  );
};
